import React, { useEffect } from 'react';
import { setPageTitle } from '../../utils/title';
import { trackEvent } from '../../utils/analytics';
import { useCart } from '../../context/CartContext';
import Breadcrumb from '../../components/Breadcrumb';
import './Solutions.scss';

const FinOps = () => {
  const { addToCart, removeFromCart, isInCart } = useCart();

  useEffect(() => {
    setPageTitle('FinOps Solutions');
  }, []);

  const handleCartAction = (solutionType, title) => {
    const itemId = `finops-${solutionType}`;
    const isItemInCart = isInCart(itemId);

    if (isItemInCart) {
      trackEvent('remove_from_cart', 'FinOps Solutions', {
        solution_name: 'FinOps',
        solution_type: solutionType,
        source_page: 'finops_solutions',
      });
      removeFromCart(itemId);
    } else {
      trackEvent('add_to_cart', 'FinOps Solutions', {
        solution_name: 'FinOps',
        solution_type: solutionType,
        source_page: 'finops_solutions',
      });
      addToCart({
        id: itemId,
        title,
        type: 'FinOps Solution',
      });
    }
  };

  const renderCartButton = (solutionType, title) => {
    const itemId = `finops-${solutionType}`;
    const isItemInCart = isInCart(itemId);

    return (
      <button
        className="card__link card__link--cart"
        onClick={() => handleCartAction(solutionType, title)}
      >
        {isItemInCart ? 'Remove from shopping cart' : 'Add to shopping cart'}
      </button>
    );
  };

  const breadcrumbs = [
    { path: '/', label: 'Home' },
    { path: '/solutions', label: 'Solutions' },
    { path: '/solutions/finops', label: 'FinOps' },
  ];

  return (
    <div className="solutions-page">
      <Breadcrumb items={breadcrumbs} />
      <section className="page-header section section--light">
        <div className="container">
          <p className="section__subtitle">AWS Cost Optimization</p>
          <h1 className="page-header__title">FinOps Solutions</h1>
          <p className="page-header__description">
            We help you optimize your AWS costs by analyzing your cloud usage, identifying
            opportunities to reduce waste, and implementing tailored strategies to maximize
            performance while minimizing expenses.
          </p>
          <blockquote className="section__quote">
            Our FinOps solutions are designed to help you achieve cost efficiency on AWS without
            compromising on performance, security, or reliability. We work with you to understand
            your business needs and implement cost optimization strategies that align with your
            goals.
          </blockquote>
        </div>
      </section>

      <section className="solutions-content section section--light">
        <div className="container">
          <div className="card solution-card">
            <h3 className="card__title">AWS Cost Analysis and Optimization</h3>
            <p className="card__description">
              We analyze your AWS usage patterns, identify cost optimization opportunities, and
              implement strategies to reduce waste while maintaining performance. This includes
              right-sizing resources, leveraging reserved instances, and optimizing storage costs.
            </p>
            {renderCartButton('cost_analysis', 'AWS Cost Analysis and Optimization')}
          </div>

          <div className="card solution-card">
            <h3 className="card__title">AWS Cost Monitoring and Reporting</h3>
            <p className="card__description">
              We set up comprehensive cost monitoring and reporting systems using AWS Cost Explorer
              and custom dashboards. This helps you track spending, identify trends, and make
              data-driven decisions about your cloud infrastructure.
            </p>
            {renderCartButton('cost_monitoring', 'AWS Cost Monitoring and Reporting')}
          </div>

          <div className="card solution-card">
            <h3 className="card__title">AWS Budget Management</h3>
            <p className="card__description">
              We help you establish and manage AWS budgets, set up alerts for cost anomalies, and
              implement automated responses to prevent cost overruns. This ensures you stay within
              your budget while maintaining operational excellence.
            </p>
            {renderCartButton('budget_management', 'AWS Budget Management')}
          </div>

          <div className="card solution-card">
            <h3 className="card__title">AWS Cost Allocation and Chargeback</h3>
            <p className="card__description">
              We implement cost allocation strategies using tags and cost centers, enabling accurate
              chargeback to different departments or projects. This helps you understand and manage
              costs at a granular level.
            </p>
            {renderCartButton('cost_allocation', 'AWS Cost Allocation and Chargeback')}
          </div>
        </div>
      </section>
    </div>
  );
};

export default FinOps;
